import { createSlice } from '@reduxjs/toolkit';
import { fetchGet, fetchPost, fetchPut, fetchDelete } from '../../helpers/fetching';
import {
  setSnackbar,
  setResults,
  setExaminationList,
  fetchResults,
  setActionToConfirm,
  sendManualReports,
  setConfirmed,
  setApplied,
  setActiveDialog,
  setTabsIndex,
  setZoomedProjection,
  setInitialImages,
  hideAllLesions,
  changeMicrocalcToShow,
  changeOpacitiesToShow,
} from '../index';
import {
  addDerivedFeaturesCC,
  addDerivedFeaturesMLO,
  addPNLEval,
  addSymmetryEval,
} from '../../helpers/derivedQualityFeatures';

const initialState = {
  qualityInRevisionMode: false,
  qualityInDrawingMode: false,
  densityInRevisionMode: false,
  diagnosticsInRevisionMode: false,

  shownQualityOverlayType: '',
  localChangesDensity: false,
  localChangesQuality: false,
  localChangesOpacities: false,
  localChangesMicrocalc: false,

  qualityEvalChanges: false,

  customDensity: {
    rcc: null,
    lcc: null,
    rmlo: null,
    lmlo: null,
  },

  customQuality: {
    rcc: null,
    lcc: null,
    rmlo: null,
    lmlo: null,
  },

  customOpacities: {
    rcc: null,
    lcc: null,
    rmlo: null,
    lmlo: null,
  },

  customMicrocalc: {
    rcc: null,
    lcc: null,
    rmlo: null,
    lmlo: null,
  },

  customQualityOverlays: {},

  drawing: {
    radius: 30,
    mode: '',
  },

  noTypeRectangles: {
    rcc: [],
    lcc: [],
    rmlo: [],
    lmlo: [],
  },

  singleBoxHover: {
    findingID: null,
    lesionID: null,
  },

  targetLesionId: null,
  draggingLesionId: null,
  justEditedRows: null,

  savingPending: false,

  manualPnlChanges: {
    rcc: false,
    lcc: false,
  },
};

export const labelingSlice = createSlice({
  name: 'labeling',
  initialState,
  reducers: {
    setQualityInRevisionMode: (state, action) => {
      state.qualityInRevisionMode = action.payload;
    },
    setQualityInDrawingMode: (state, action) => {
      state.qualityInDrawingMode = action.payload;
    },
    setDensityInRevisionMode: (state, action) => {
      state.densityInRevisionMode = action.payload;
    },
    setManualPnlChange: (state, action) => {
      const { projection, isManual } = action.payload;
      state.manualPnlChanges[projection.toLowerCase()] = isManual;
    },
    resetManualPnlChanges: (state) => {
      state.manualPnlChanges = {
        rcc: false,
        lcc: false,
      };
    },
    setDiagnosticsInRevisionMode: (state, action) => {
      state.diagnosticsInRevisionMode = action.payload;
    },
    setCustomDensity: (state, action) => {
      state.customDensity = action.payload;
    },
    setCustomQuality: (state, action) => {
      state.customQuality = action.payload;
    },
    setCustomMicrocalc: (state, action) => {
      state.customMicrocalc = action.payload;
    },
    setCustomOpacities: (state, action) => {
      state.customOpacities = action.payload;
    },
    setDrawing: (state, action) => {
      state.drawing = action.payload;
    },
    setInitialLabeling: (state, action) => {
      const newState = { ...initialState };
      if (action.payload) {
        for (let exception of action.payload) {
          if (exception in state) newState[exception] = state[exception];
        }
      }
      return newState;
    },

    setShownQualityOverlayType: (state, actions) => {
      state.shownQualityOverlayType = actions.payload;
    },

    setQualityLocalChanges: (state, actions) => {
      state.localChangesQuality = actions.payload;
    },

    setMicrocalcLocalChanges: (state, actions) => {
      state.localChangesMicrocalc = actions.payload;
    },
    setOpacitiesLocalChanges: (state, actions) => {
      state.localChangesOpacities = actions.payload;
    },
    setDensityLocalChanges: (state, actions) => {
      state.localChangesDensity = actions.payload;
    },

    setQualityEvalChanged: (state, actions) => {
      state.qualityEvalChanges = actions.payload;
    },
    setSavingPending: (state, actions) => {
      state.savingPending = actions.payload;
    },
    setCustomQualityOverlays: (state, actions) => {
      state.customQualityOverlays = actions.payload;
    },
    setDiscardedLabeling: (state) => {
      return { ...initialState };
    },

    setSingleBoxHover: (state, actions) => {
      state.singleBoxHover = actions.payload;
    },

    setTargetLesionId: (state, actions) => {
      state.targetLesionId = actions.payload;
    },

    setDraggingLesionId: (state, actions) => {
      state.draggingLesionId = actions.payload;
    },

    setJustEditedRows: (state, actions) => {
      state.justEditedRows = actions.payload;
    },

    setInitialRectangles: (state) => {
      state.noTypeRectangles = initialState.noTypeRectangles;
    },

    setNoTypeRectangles: (state, actions) => {
      state.noTypeRectangles = actions.payload;
    },
  },
});

export const {
  setCustomDensity,
  setCustomQuality,
  setCustomOpacities,
  setCustomMicrocalc,
  setCustomQualityOverlays,

  setQualityInRevisionMode,
  setQualityInDrawingMode,
  setDensityInRevisionMode,
  setDiagnosticsInRevisionMode,

  setDrawing,

  setInitialLabeling,

  setShownQualityOverlayType,

  setQualityLocalChanges,
  setMicrocalcLocalChanges,
  setOpacitiesLocalChanges,
  setDensityLocalChanges,
  setQualityEvalChanged,
  setSavingPending,

  setSingleBoxHover,
  setTargetLesionId,
  setDraggingLesionId,
  setJustEditedRows,

  setNoTypeRectangles,

  setInitialRectangles,
  setManualPnlChange,
  resetManualPnlChanges,
} = labelingSlice.actions;

export default labelingSlice.reducer;

// Derived
export const updateCustomDensity = (projection, evaluation) => {
  return (dispatch, getState) => {
    const newCustomDensity = {
      ...getState().customDensity,
      [projection]: evaluation,
    };
    dispatch(setCustomDensity(newCustomDensity));
  };
};

export const updateCustomQuality = (projection, evaluation) => {
  if (Object.keys(evaluation).length === 0) {
    evaluation = undefined;
  }
  return (dispatch, getState) => {
    const newCustomQuality = {
      ...getState().labeling.customQuality,
      [projection.toLowerCase()]: evaluation,
    };
    dispatch(setCustomQuality(newCustomQuality));
  };
};

export const updateCustomOpacity = (projection, evaluation) => {
  return (dispatch, getState) => {
    const newCustomOpacity = {
      ...getState().customOpacity,
      [projection]: evaluation,
    };
    dispatch(setCustomOpacities(newCustomOpacity));
  };
};

export const updateCustomMicrocalc = (projection, evaluation) => {
  return (dispatch, getState) => {
    const newCustomMicrocalc = {
      ...getState().customMicrocalc,
      [projection]: evaluation,
    };
    dispatch(setCustomMicrocalc(newCustomMicrocalc));
  };
};

export const updateCustomQualityWithDerrived = (projection, newResult, field) => {
  return (dispatch, getState) => {
    const projections = getState().results.projections;
    const { pect_level_classes } = getState().global_settings.quality_system.quality_features;
    const {
      nipple_angle_lateral_thresholds,
      nipple_angle_medial_thresholds,
      symmetry_thresholds,
      pnl_diff_thresholds,
    } = getState().global_settings?.quality_system.quality_thresholds;

    const nippleAngleThreshold =
      newResult.NippleAngle < 0 ? nipple_angle_medial_thresholds : nipple_angle_lateral_thresholds;

    if (projection === 'RCC') {
      const resultMLO = { ...getState().labeling.customQuality.rmlo };
      const resultLCC = { ...getState().labeling.customQuality.lcc };
      addDerivedFeaturesCC(newResult, projections.rcc, true, nippleAngleThreshold, field);
      addPNLEval(newResult, resultMLO, pnl_diff_thresholds);
      // addSymmetryEval(resultLCC, newResult, symmetry_thresholds);
      dispatch(updateCustomQuality('RCC', newResult));
      dispatch(updateCustomQuality('LCC', resultLCC));
    } else if (projection === 'LCC') {
      const resultMLO = { ...getState().labeling.customQuality.lmlo };
      const resultRCC = { ...getState().labeling.customQuality.rcc };
      addDerivedFeaturesCC(newResult, projections.lcc, false, nippleAngleThreshold, field);
      addPNLEval(newResult, resultMLO, pnl_diff_thresholds);
      // addSymmetryEval(newResult, resultRCC, symmetry_thresholds);
      dispatch(updateCustomQuality('LCC', newResult));
      dispatch(updateCustomQuality('RCC', resultRCC));
    } else if (projection === 'RMLO') {
      const resultCC = { ...getState().labeling.customQuality.rcc };
      const resultLMLO = { ...getState().labeling.customQuality.lmlo };
      addDerivedFeaturesMLO(newResult, projections.rmlo, true, pnl_diff_thresholds, pect_level_classes);
      addPNLEval(resultCC, newResult, pnl_diff_thresholds);
      addSymmetryEval(resultLMLO, newResult, symmetry_thresholds);
      dispatch(updateCustomQuality('RMLO', newResult));
      dispatch(updateCustomQuality('RCC', resultCC));
      dispatch(updateCustomQuality('LMLO', resultLMLO));
    } else if (projection === 'LMLO') {
      const resultCC = { ...getState().labeling.customQuality.lcc };
      const resultRMLO = { ...getState().labeling.customQuality.rmlo };
      addDerivedFeaturesMLO(newResult, projections.lmlo, false, pnl_diff_thresholds, pect_level_classes);
      addPNLEval(resultCC, newResult, pnl_diff_thresholds);
      addSymmetryEval(newResult, resultRMLO, symmetry_thresholds);
      dispatch(updateCustomQuality('LMLO', newResult));
      dispatch(updateCustomQuality('LCC', resultCC));
      dispatch(updateCustomQuality('RMLO', resultRMLO));
    }
  };
};

export const updateCustomQualityFeature = (projection, field, newValue, withDerrived = false) => {
  return (dispatch, getState) => {
    const customQuality = getState().labeling?.customQuality?.[projection.toLowerCase()];
    const newCustomQuality = {
      ...customQuality,
      [field]: newValue,
    };

    if (withDerrived) {
      dispatch(updateCustomQualityWithDerrived(projection, newCustomQuality, field));
    } else {
      dispatch(updateCustomQuality(projection, newCustomQuality));
    }
    dispatch(setQualityLocalChanges(true));
  };
};

export const switchQuality = (projection, field, baseClasses, withDerrived = false) => {
  return (dispatch, getState) => {
    const customQuality = getState().labeling.customQuality[projection.toLowerCase()];
    const projObject = getState().results.projections[projection.toLowerCase()];

    if (!projObject) return;

    let idx = baseClasses.indexOf(customQuality[field]);
    idx = (idx + 1) % baseClasses.length;

    // Mark PNL to prevent override
    if (field === 'PNL') {
      dispatch(setManualPnlChange({ projection, isManual: true }));
    }

    dispatch(updateCustomQualityFeature(projection, field, baseClasses[idx], withDerrived));
  };
};

export const switchNippleTiltQuality = (projection, field, base, withDerived = false) => {
  return (dispatch, getState) => {
    const customQuality = getState().labeling.customQuality[projection.toLowerCase()];
    const nippleAngle = getState().labeling.customQuality[projection.toLowerCase()]?.NippleAngle;

    if (!customQuality) return;

    const quality = base;

    let idx = quality.indexOf(customQuality[field]);
    idx = (idx + 1) % base.length;

    if (idx === 0) {
      const newNippleAngle = nippleAngle < 0 ? Math.abs(nippleAngle) : -nippleAngle;
      dispatch(updateCustomQualityFeature(projection, 'NippleAngle', newNippleAngle));
    } else if (nippleAngle === 0) {
      const newNippleAngle = nippleAngle < 0 ? Number.MIN_VALUE : -Number.MIN_VALUE;
      dispatch(updateCustomQualityFeature(projection, 'NippleAngle', newNippleAngle));
    }
    dispatch(updateCustomQualityFeature(projection, field, quality[idx], withDerived));
  };
};

export const switchQualityEvaluation = (projection, t) => {
  return (dispatch, getState) => {
    const customQuality = getState().labeling.customQuality[projection.toLowerCase()];
    const quality = getState().global_settings.quality_system?.quality_values;

    if (quality) {
      let idx = quality?.indexOf(customQuality.quality);
      idx = (idx + 1) % quality.length;
      dispatch(setQualityEvalChanged(true));
      dispatch(updateCustomQualityFeature(projection, 'quality', quality[idx]));
      dispatch(updateCustomQualityFeature(projection, 'quality_explanation', ['Reviewed by a user']));
    } else
      dispatch(
        setSnackbar({
          msg: 'Confirm.contact_brayz',
          severity: 'info',
        }),
      );
  };
};

export const updateDensity = (projection, newClass, t) => {
  projection = projection.toLowerCase();

  return (dispatch, getState) => {
    const proj = getState().results.projections[projection.toLowerCase()];
    if (!proj) return;

    const newProba = [0, 0, 0, 0];
    const densities = ['A', 'B', 'C', 'D'];
    const idx = densities.indexOf(newClass);
    if (idx < 0) {
      dispatch(
        setSnackbar({
          msg: 'Confirm.density_annotation_failed',
          severity: 'error',
        }),
      );
      return;
    }
    newProba[idx] = 100;

    const newCustomDensity = { ...getState().labeling.customDensity };

    if (!(projection in newCustomDensity)) {
      dispatch(
        setSnackbar({
          msg: 'Confirm.density_annotation_failed_proj',
          severity: 'error',
        }),
      );
      return;
    }

    newCustomDensity[projection] = {
      ...newCustomDensity[projection],
      DensClass: newClass,
      DensProba: newProba,
    };

    dispatch(setCustomDensity(newCustomDensity));
    dispatch(setDensityLocalChanges(true));
  };
};

export const setEvaluation = (username, newEval, confirm = true) => {
  return (dispatch, getState) => {
    const results = getState().results;
    const newResults = {
      ...results,
      evaluations: {
        ...results.evaluations,
        [username]: newEval,
      },
    };
    if (confirm) {
      newResults.confirmed_by = null;
      newResults.revised_by = username;
    }
    dispatch(setResults(newResults));
  };
};

const setEvaluations = (newEvaluations, newRevisedBy) => {
  return (dispatch, getState) => {
    const results = getState().results;
    const newResults = {
      ...results,
      evaluations: newEvaluations,
      revised_by: newRevisedBy,
    };
    dispatch(setResults(newResults));
  };
};

export const updateCustomQualityOverlays = (overlayType, proj, imageDataUrl) => {
  return (dispatch, getState) => {
    const customOverlays = getState().labeling.customQualityOverlays;

    const newCustomOverlays = {
      ...customOverlays,
      [proj]: {
        ...customOverlays[proj],
        [overlayType]: imageDataUrl,
      },
    };
    dispatch(setCustomQualityOverlays(newCustomOverlays));
  };
};

export const updateCustomDiagnostics = (removeMicrocalc, removeOpacities, proj) => {
  return (dispatch, getState) => {
    const customMicrocalc = getState().labeling.customMicrocalc;
    const customOpacities = getState().labeling.customOpacities;

    if (removeMicrocalc || removeOpacities) {
      let newCustomMicrocalc = customMicrocalc;
      let newCustomOpacities = customOpacities;

      if (removeMicrocalc) {
        newCustomMicrocalc = {
          ...newCustomMicrocalc,
          [proj]: {
            ...newCustomMicrocalc[proj],
            birads2: null,
            birads3: null,
            birads4: null,
            birads5: null,
          },
        };
      }

      if (removeOpacities) {
        newCustomOpacities = {
          ...newCustomOpacities,
          [proj]: {
            ...newCustomOpacities[proj],
            birads2: null,
            birads3: null,
            birads4: null,
            birads5: null,
          },
        };
      }

      dispatch(setCustomMicrocalc(newCustomMicrocalc));
      dispatch(setCustomOpacities(newCustomOpacities));
    }
  };
};

export const customResults = (module, appliedResults, getState) => {
  const results = getState().results;

  const customEvaluationResults = {
    rcc: results?.evaluations?.[module]?.[appliedResults]?.rcc,
    lcc: results?.evaluations?.[module]?.[appliedResults]?.lcc,
    rmlo: results?.evaluations?.[module]?.[appliedResults]?.rmlo,
    lmlo: results?.evaluations?.[module]?.[appliedResults]?.lmlo,
  };

  return customEvaluationResults;
};

export const setCustomResults = (results) => {
  return (dispatch, getState) => {
    const appliedDensity = getState().results.shown_density || getState().results.applied_density;
    const appliedQuality = getState().results.shown_quality || getState().results.applied_quality;
    const appliedMicrocalc = getState().results.shown_microcalc || getState().results.applied_microcalc;
    const appliedOpacities = getState().results.shown_opacities || getState().results.applied_opacities;

    const customDensity = customResults('density', appliedDensity, getState);
    const customQuality = customResults('quality', appliedQuality, getState);
    const customMicrocalc = customResults('microcalc', appliedMicrocalc, getState);
    const customOpacity = customResults('opacities', appliedOpacities, getState);

    dispatch(setCustomDensity(customDensity));
    dispatch(setCustomQuality(customQuality));
    dispatch(setCustomMicrocalc(customMicrocalc));
    dispatch(setCustomOpacities(customOpacity));
  };
};

const sendReport = (type, currentExam, loggedUser, t, dispatch, confirmAction = false) => {
  dispatch(
    setActionToConfirm({
      action: () => dispatch(sendManualReports(type, currentExam, loggedUser, t, confirmAction)),
      text: t('revision.send_reports'),
      buttonOk: t('revision.send_report'),
      butonCancel: t('revision.only_save'),
    }),
  );
};

// Fetching
export const confirmEvaluation = (created_by, type, state, t) => {
  return async (dispatch, getState) => {
    const { currentExam, exam_list } = getState().examinations;
    const {
      confirmed_density,
      confirmed_quality,
      confirmed_opacities,
      confirmed_microcalc,
      report_sending_rules,
      fhir_sending_rules,
      hl7_sending_rules,
    } = getState().results;
    const { diagnostics_report_manual, quality_report_manual, score_card_manual, structured_report_manual } =
      report_sending_rules;
    const { fhir_manual } = fhir_sending_rules || {};
    const { hl7_manual } = hl7_sending_rules || {};

    created_by = state ? created_by : 'bbox';
    const loggedUser = localStorage.getItem('username');
    const confirmed_by = state ? loggedUser : null;

    const body = {
      exam_id: currentExam,
      created_by,
      type,
      confirmed_by,
      loggedUser,
    };

    const response = await fetchPost('labels', body);

    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
      return;
    }

    updateExamList(exam_list, currentExam, type, state, dispatch);

    dispatch(setConfirmed(type, confirmed_by));

    if (response.data?.created_by) {
      created_by = response.data.created_by;
    }
    dispatch(setApplied(type, created_by));

    dispatch(fetchResults());

    dispatch(
      setSnackbar({
        msg: `api_messages.${response.msg}`,
        severity: 'success',
      }),
    );

    // Send manual reports after confirmation
    if (state) {
      if (type === 'quality' || type === 'density') {
        if (quality_report_manual || score_card_manual || fhir_manual || hl7_manual) {
          sendReport(type, currentExam, loggedUser, t, dispatch, true);
        }
      } else if (
        (type === 'microcalc' && confirmed_opacities) ||
        (type === 'opacities' && confirmed_microcalc)
      ) {
        if (diagnostics_report_manual || score_card_manual || fhir_manual || hl7_manual) {
          sendReport(type, currentExam, loggedUser, t, dispatch, true);
        }
      }
    }
  };
};

export const saveEvaluation = (type, t) => {
  return async (dispatch, getState) => {
    // Send report
    dispatch(setSavingPending(true));

    const {
      customDensity,
      customQuality,
      customOpacities,
      customMicrocalc,
      qualityEvalChanges,
      customQualityOverlays,
    } = getState().labeling;
    const { currentExam, exam_list } = getState().examinations;
    const { report_sending_rules, fhir_sending_rules, hl7_sending_rules } = getState().results;
    const username = localStorage.getItem('username');
    const { structured_report_manual, score_card_manual, quality_report_manual, diagnostics_report_manual } =
      report_sending_rules;
    const { fhir_manual } = fhir_sending_rules || {};
    const { hl7_manual } = hl7_sending_rules || {};

    // Request body
    const body = {
      exam_id: currentExam,
      result_type: type,
      username,
      overlayImages: customQualityOverlays,
      updateQuality: !qualityEvalChanges,
    };

    if (type === 'density') {
      body['results'] = {
        rcc: customDensity.rcc,
        lcc: customDensity.lcc,
        rmlo: customDensity.rmlo,
        lmlo: customDensity.lmlo,
      };
    } else if (type === 'quality') {
      body['results'] = {
        rcc: customQuality.rcc,
        lcc: customQuality.lcc,
        rmlo: customQuality.rmlo,
        lmlo: customQuality.lmlo,
      };
    } else if (type === 'opacities') {
      body['results'] = {
        rcc: customOpacities.rcc,
        lcc: customOpacities.lcc,
        rmlo: customOpacities.rmlo,
        lmlo: customOpacities.lmlo,
      };
    } else if (type === 'microcalc') {
      body['results'] = {
        rcc: customMicrocalc.rcc,
        lcc: customMicrocalc.lcc,
        rmlo: customMicrocalc.rmlo,
        lmlo: customMicrocalc.lmlo,
      };
    } else {
      dispatch(
        setSnackbar({
          msg: `${t('revision.bad_eval_type')}: ${type}`,
          severity: 'warning',
        }),
      );
      return;
    }

    const response = await fetchPut('revision', body, 'bbox');
    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
      dispatch(setSavingPending(false));
      return;
    }

    dispatch(
      setSnackbar({
        msg: 'revision.revison_saved',
        severity: 'success',
      }),
    );

    dispatch(setMicrocalcLocalChanges(false));
    dispatch(setOpacitiesLocalChanges(false));
    dispatch(setDensityLocalChanges(false));
    dispatch(setQualityLocalChanges(false));
    dispatch(setQualityEvalChanged(false));
    dispatch(setCustomQualityOverlays({}));
    dispatch(resetManualPnlChanges());

    const typesToUpdate = type === 'microcalc' || type === 'opacities' ? ['microcalc', 'opacities'] : [type];

    updateExamList(exam_list, currentExam, typesToUpdate, true, dispatch);

    // Fetch new result
    dispatch(fetchResults());

    dispatch(setSavingPending(false));
    dispatch(setQualityInRevisionMode(false));
    dispatch(setQualityInDrawingMode(false));
    dispatch(setDensityInRevisionMode(false));
    dispatch(setDiagnosticsInRevisionMode(false));

    // Sent manual reports after revising

    if (type === 'quality' || type === 'density') {
      if (
        quality_report_manual ||
        structured_report_manual ||
        score_card_manual ||
        fhir_manual ||
        hl7_manual
      ) {
        sendReport(type, currentExam, username, t, dispatch, false);
      }
    } else if (type === 'microcalc' || type === 'opacities') {
      if (
        diagnostics_report_manual ||
        structured_report_manual ||
        score_card_manual ||
        fhir_manual ||
        hl7_manual
      ) {
        sendReport(type, currentExam, username, t, dispatch, false);
      }
    }
  };
};

export const deleteEvaluation = (type, created_by, t) => {
  return async (dispatch, getState) => {
    if (created_by === 'bbox') {
      dispatch(
        setSnackbar({
          msg: 'Confirm.error_delete',
          severity: 'warning',
        }),
      );
      return;
    }

    const state = getState();
    const { currentExam, exam_list } = state.examinations;
    const resultsState = state.results;

    const deleteSingleType = async (labelType) => {
      const applied = resultsState[`applied_${labelType}`];

      return await fetchDelete('labels', {
        exam_id: currentExam,
        created_by,
        type: labelType,
        isApplied: created_by === applied,
      });
    };

    const typesToDelete = type === 'diagnostics' ? ['microcalc', 'opacities'] : [type];

    for (const labelType of typesToDelete) {
      const response = await deleteSingleType(labelType);

      if (!response?.success) {
        dispatch(
          setSnackbar({
            msg: 'Confirm.delete_fail',
            severity: 'error',
            msgParams: {
              message: response?.msg || '',
            },
          }),
        );
      }

      updateExamList(exam_list, currentExam, typesToDelete, false, dispatch);
    }

    dispatch(fetchResults());

    dispatch(
      setSnackbar({
        msg: 'Confirm.deleted',
        severity: 'success',
      }),
    );
  };
};

export const setPostSurgery = (exam_id, view_position, postop_arr) => {
  return async (dispatch, getState) => {
    const response = await fetchPut('postop', {
      exam_id,
      view_position,
      postop_arr,
    });

    if (response.success) {
      dispatch(fetchResults());
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'success',
        }),
      );
    } else
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
  };
};

// Relabeling helpers

export const updateJustEditedRows = (rows) => {
  return (dispatch, getState) => {
    const rowIds = Array.isArray(rows) ? rows : rows ? [rows] : [];

    setTimeout(() => {
      dispatch(setJustEditedRows(rows));
    }, 500);

    setTimeout(() => {
      dispatch(setJustEditedRows(null));
    }, 5000);
  };
};

export const updateNoTypeRectangles = (proj, rectangles) => {
  return (dispatch, getState) => {
    const noTypeRectangles = getState().labeling.noTypeRectangles || {};
    const currentRectangles = noTypeRectangles[proj] || [];

    const newState = {
      ...noTypeRectangles,
      [proj]: rectangles,
    };
    dispatch(setNoTypeRectangles(newState));
  };
};

// Helpers
function updateExamList(exam_list, currentExam, type, state, dispatch) {
  const idx = exam_list.findIndex((element) => element.id === currentExam);
  const newExamList = [...exam_list];
  const newElement = { ...newExamList[idx] };
  const typeToEdit = Array.isArray(type) ? type.map((t) => `${t}_confirmed`) : [`${type}_confirmed`];
  if (newExamList[idx]) {
    typeToEdit.forEach((finalType) => {
      newElement[finalType] = state ? localStorage.getItem('username') : null;
    });
  }
  newExamList[idx] = newElement;
  dispatch(setExaminationList(newExamList));
}

//Handles revision confirmation for all revision modes (quality, density, diagnostics)

export const handleRevisionConfirm = (t) => {
  return async (dispatch, getState) => {
    const { qualityInRevisionMode, densityInRevisionMode, diagnosticsInRevisionMode } = getState().labeling;
    const {
      applied_quality,
      confirmed_quality,
      applied_density,
      confirmed_density,
      shown_quality,
      shown_density,
      shown_opacities,
      shown_microcalc,
      applied_microcalc,
      applied_opacities,
      confirmed_microcalc,
      confirmed_opacities,
    } = getState().results;

    if (qualityInRevisionMode) {
      const state = shown_quality === applied_quality && confirmed_quality;
      dispatch(confirmEvaluation(shown_quality, 'quality', !state, t));
      dispatch(setQualityInDrawingMode(false));
      dispatch(setQualityLocalChanges(false));
    }

    if (densityInRevisionMode) {
      const state = shown_density === applied_density && confirmed_density;
      dispatch(confirmEvaluation(shown_density, 'density', !state, t));
      dispatch(hideAllLesions());
      dispatch(setDensityLocalChanges(false));
    }

    if (diagnosticsInRevisionMode) {
      const microcalcState = shown_microcalc === applied_microcalc && confirmed_microcalc;
      const opacitiesState = shown_opacities === applied_opacities && confirmed_opacities;

      const microcalcActionState = microcalcState === opacitiesState ? !microcalcState : microcalcState;
      const opacitiesActionState = opacitiesState === microcalcState ? !opacitiesState : opacitiesState;

      if (opacitiesActionState && !microcalcActionState) {
        dispatch(confirmEvaluation(shown_microcalc, 'microcalc', opacitiesActionState, t));
        dispatch(confirmEvaluation(shown_opacities, 'opacities', opacitiesActionState, t));
      } else if (microcalcActionState && !opacitiesActionState) {
        dispatch(confirmEvaluation(shown_microcalc, 'microcalc', microcalcActionState, t));
        dispatch(confirmEvaluation(shown_opacities, 'opacities', microcalcActionState, t));
      } else {
        dispatch(confirmEvaluation(shown_microcalc, 'microcalc', !microcalcState, t));
        dispatch(confirmEvaluation(shown_opacities, 'opacities', !opacitiesState, t));
      }

      dispatch(setDiagnosticsInRevisionMode(false));
      dispatch(
        setCustomOpacities(getState().results?.evaluations?.opacities?.[getState().results.shown_opacities]),
      );
      dispatch(
        setCustomMicrocalc(getState().results?.evaluations?.microcalc?.[getState().results.shown_microcalc]),
      );
      dispatch(setInitialImages());
      dispatch(setMicrocalcLocalChanges(false));
      dispatch(setOpacitiesLocalChanges(false));
      dispatch(setTabsIndex(0));
    }

    dispatch(setTabsIndex(0));
  };
};

//Handles exiting revision mode with unsaved changes check

export const handleRevisionExit = () => {
  return async (dispatch, getState) => {
    const {
      localChangesDensity,
      localChangesMicrocalc,
      localChangesOpacities,
      localChangesQuality,
      qualityInRevisionMode,
      densityInRevisionMode,
      diagnosticsInRevisionMode,
    } = getState().labeling;
    const { applied_quality, applied_density } = getState().results;

    if (localChangesDensity || localChangesMicrocalc || localChangesOpacities || localChangesQuality) {
      dispatch(setActiveDialog('unsavedChanges'));
    } else {
      if (qualityInRevisionMode) {
        dispatch(setQualityInRevisionMode(false));
        dispatch(setCustomQualityOverlays({}));
        dispatch(setQualityLocalChanges(false));
        dispatch(setQualityInDrawingMode(false));
      } else if (densityInRevisionMode) {
        dispatch(setDensityInRevisionMode(false));
        dispatch(setDensityLocalChanges(false));
        dispatch(
          setCustomDensity(getState().results?.evaluations?.density?.[getState().results.shown_density]),
        );
      } else if (diagnosticsInRevisionMode) {
        dispatch(setDiagnosticsInRevisionMode(false));
        dispatch(setMicrocalcLocalChanges(false));
        dispatch(
          setCustomMicrocalc(
            getState().results?.evaluations?.microcalc?.[getState().results.shown_microcalc],
          ),
        );
        dispatch(setOpacitiesLocalChanges(false));
        dispatch(
          setCustomOpacities(
            getState().results?.evaluations?.opacities?.[getState().results.shown_opacities],
          ),
        );
      }
      dispatch(setTabsIndex(0));
      dispatch(hideAllLesions());
    }
  };
};

// Handle Discard action
export const handleRevisionDiscard = (undoChanges = false) => {
  return async (dispatch, getState) => {
    const { activeDialog } = getState().gui;
    const {
      localChangesQuality,
      diagnosticsInRevisionMode,
      localChangesDensity,
      localChangesMicrocalc,
      localChangesOpacities,
    } = getState().labeling;
    const results = getState().results;
    const {
      applied_density,
      applied_opacities,
      applied_microcalc,
      shown_density,
      shown_microcalc,
      shown_opacities,
      shown_quality,
      applied_quality,
    } = results;

    const densityShown = shown_density || applied_density;
    const microcalcShown = shown_microcalc || applied_microcalc;
    const opacitiesShown = shown_opacities || applied_opacities;
    const qualityShown = shown_quality || applied_quality;

    const resultMicrocalcEvaluation = results?.evaluations?.microcalc?.[microcalcShown];
    const resultOpacitiesEvaluation = results?.evaluations?.opacities?.[opacitiesShown];
    const resultDensityEvaluation = results?.evaluations?.density?.[densityShown];
    const resultQualityEvaluation = results?.evaluations?.quality?.[qualityShown];

    if (localChangesQuality) {
      if (undoChanges) {
        dispatch(setQualityInRevisionMode(true));
        dispatch(setTabsIndex(2));
      } else {
        dispatch(setQualityInRevisionMode(false));
        dispatch(setTabsIndex(1));
      }
      dispatch(setCustomQualityOverlays({}));
      dispatch(setQualityLocalChanges(false));
      dispatch(setQualityInDrawingMode(false));
      dispatch(setShownQualityOverlayType(''));
      dispatch(resetManualPnlChanges());
      dispatch(setCustomQuality(resultQualityEvaluation));
    } else if (localChangesMicrocalc || localChangesOpacities || diagnosticsInRevisionMode) {
      if (undoChanges) {
        dispatch(setDiagnosticsInRevisionMode(true));
        dispatch(setInitialRectangles());
      } else {
        dispatch(setDiagnosticsInRevisionMode(false));
      }
      dispatch(setOpacitiesLocalChanges(false));
      dispatch(setCustomOpacities(resultOpacitiesEvaluation));
      dispatch(setMicrocalcLocalChanges(false));
      dispatch(setCustomMicrocalc(resultMicrocalcEvaluation));
    } else if (localChangesDensity) {
      if (undoChanges) {
        dispatch(setDensityInRevisionMode(true));
      } else {
        dispatch(setDensityInRevisionMode(false));
      }
      dispatch(setCustomDensity(resultDensityEvaluation));
      dispatch(setDensityLocalChanges(false));
    }

    if (activeDialog === 'unsavedChanges' && !localChangesQuality) {
      dispatch(setTabsIndex(0));
    }

    dispatch(setInitialImages());
    dispatch(setActiveDialog(null));
    dispatch(setZoomedProjection(null));
    dispatch(setQualityInDrawingMode(false));
    dispatch(setCustomResults(results));
  };
};

//Handles saving results in revision mode

export const handleRevisionSave = (t) => {
  return async (dispatch, getState) => {
    const { qualityInRevisionMode, densityInRevisionMode, diagnosticsInRevisionMode } = getState().labeling;

    if (qualityInRevisionMode) {
      dispatch(saveEvaluation('quality', t));
      dispatch(setQualityInRevisionMode(false));
      dispatch(setInitialImages());
      dispatch(setQualityLocalChanges(false));
      dispatch(setQualityInDrawingMode(false));
      dispatch(setTabsIndex(1));
    } else if (densityInRevisionMode) {
      dispatch(saveEvaluation('density', t));
      dispatch(setDensityLocalChanges(false));
      dispatch(setTabsIndex(0));
    } else if (diagnosticsInRevisionMode) {
      dispatch(saveEvaluation('opacities', t));
      dispatch(saveEvaluation('microcalc', t));
      dispatch(setDiagnosticsInRevisionMode(false));
      dispatch(setInitialImages());
      dispatch(setMicrocalcLocalChanges(false));
      dispatch(setOpacitiesLocalChanges(false));
      dispatch(setTabsIndex(0));
    }
    dispatch(setZoomedProjection(null));
  };
};

export const resetHoverState = () =>
  setSingleBoxHover({
    findingID: null,
    lesionID: null,
  });
